"use client"
import type React from "react"
import { useState, useEffect } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Card,
  CardContent,
  Avatar,
  Chip,
  Grid,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  TextField,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Paper,
  Autocomplete,
} from "@mui/material"
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
} from "@mui/icons-material"
import { recruitmentChecklist } from "./data/recruitmentChecklist"
import { hrOutsourcingServices } from "./data/services"
import { ServiceRequest, ChecklistItem, ProjectData } from "./types/services"
// import type { ServiceRequest, ChecklistItem, ProjectData } from "../types/services"
// import { hrOutsourcingServices } from "../data/services"
// import { recruitmentChecklist } from "../data/recruitmentChecklist"

interface ServiceRequestModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (request: Omit<ServiceRequest, "id" | "createdAt" | "status">) => void
}

// Mock tags for autocomplete
const availableTags = [
  "Frontend Development",
  "Backend Development",
  "Full Stack",
  "Mobile Development",
  "UI/UX Design",
  "Data Science",
  "Machine Learning",
  "DevOps",
  "Quality Assurance",
  "Project Management",
  "Marketing",
  "Sales",
  "Customer Support",
  "Finance",
  "HR",
  "Operations",
]

export const ServiceRequestModal: React.FC<ServiceRequestModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const [step, setStep] = useState(0)
  const [serviceType, setServiceType] = useState<"recruitment" | "hr-outsourcing" | "">("")
  const [selectedServices, setSelectedServices] = useState<string[]>([])
  const [serviceItems, setServiceItems] = useState<{ [serviceId: string]: ChecklistItem[] }>({})
  const [customRequirements, setCustomRequirements] = useState<{ [serviceId: string]: string }>({})
  const [recruitmentItems, setRecruitmentItems] = useState<ChecklistItem[]>([])
  const [recruitmentCustomRequirements, setRecruitmentCustomRequirements] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [projectData, setProjectData] = useState<ProjectData>({
    projectName: "",
    tags: [],
    budget: "",
    description: "",
    urgency: "medium",
    timeline: "",
  })

  const getSteps = () => {
    if (serviceType === "recruitment") {
      return ["Service Type", "Project Details"]
    }
    return ["Service Type", "Service Selection"]
  }

  const steps = getSteps()

  useEffect(() => {
    if (isOpen) {
      setStep(0)
      setServiceType("")
      setSelectedServices([])
      setServiceItems({})
      setCustomRequirements({})
      setRecruitmentItems(recruitmentChecklist.map((item) => ({ ...item, selected: false })))
      setRecruitmentCustomRequirements("")
      setProjectData({
        projectName: "",
        tags: [],
        budget: "",
        description: "",
        urgency: "medium",
        timeline: "",
      })
    }
  }, [isOpen])

  const handleServiceTypeSelect = (type: "recruitment" | "hr-outsourcing") => {
    setServiceType(type)
    setStep(1) // Go to next step for both types
  }

  const handleServiceToggle = (serviceId: string) => {
    setSelectedServices((prev) => {
      const newSelected = prev.includes(serviceId) ? prev.filter((id) => id !== serviceId) : [...prev, serviceId]

      if (!prev.includes(serviceId)) {
        const service = hrOutsourcingServices.find((s) => s.id === serviceId)
        if (service) {
          setServiceItems((prevItems) => ({
            ...prevItems,
            [serviceId]: service.checklist.map((item) => ({ ...item, selected: false })),
          }))
        }
      } else {
        setServiceItems((prevItems) => {
          const newItems = { ...prevItems }
          delete newItems[serviceId]
          return newItems
        })
        setCustomRequirements((prev) => {
          const newCustom = { ...prev }
          delete newCustom[serviceId]
          return newCustom
        })
      }

      return newSelected
    })
  }

  const handleChecklistToggle = (serviceId: string, itemId: string) => {
    setServiceItems((prev) => ({
      ...prev,
      [serviceId]: prev[serviceId].map((item) => (item.id === itemId ? { ...item, selected: !item.selected } : item)),
    }))
  }

  const handleRecruitmentChecklistToggle = (itemId: string) => {
    setRecruitmentItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, selected: !item.selected } : item)),
    )
  }

  const handleCustomRequirementChange = (serviceId: string, value: string) => {
    setCustomRequirements((prev) => ({
      ...prev,
      [serviceId]: value,
    }))
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    const selectedItems: { [serviceId: string]: string[] } = {}

    if (serviceType === "hr-outsourcing") {
      // Handle HR outsourcing services
      Object.entries(serviceItems).forEach(([serviceId, items]) => {
        const selected = items.filter((item) => item.selected).map((item) => item.name)
        if (selected.length > 0) {
          selectedItems[serviceId] = selected
        }

        if (customRequirements[serviceId]) {
          selectedItems[serviceId] = [...(selectedItems[serviceId] || []), customRequirements[serviceId]]
        }
      })
    }

    const request: Omit<ServiceRequest, "id" | "createdAt" | "status"> = {
      serviceType,
      selectedServices: serviceType === "recruitment" ? ["recruitment"] : selectedServices,
      selectedItems,
      projectData: serviceType === "recruitment" ? projectData : undefined,
    }

    try {
      await onSubmit(request)
    } catch (error) {
      console.error("Error submitting request:", error)
      alert("Failed to submit request. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const canProceed = () => {
    if (step === 0) return serviceType !== ""
    if (serviceType === "recruitment") {
      if (step === 1) return projectData.projectName && projectData.tags.length > 0 && projectData.budget
    } else {
      if (step === 1) return selectedServices.length > 0
    }
    return false
  }

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { minHeight: "80vh" } }}>
      <DialogTitle
        sx={{ bgcolor: "#000", color: "white", display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Service Request
        </Typography>
        <IconButton onClick={onClose} sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          {/* Progress Stepper */}
          <Stepper activeStep={step} sx={{ mb: 4 }}>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel
                  sx={{
                    "& .MuiStepLabel-label": { color: index <= step ? "#000" : "#999", fontWeight: 600 },
                    "& .MuiStepIcon-root": { color: index <= step ? "#000" : "#ccc" },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          <LinearProgress
            variant="determinate"
            value={((step + 1) / steps.length) * 100}
            sx={{
              mb: 4,
              height: 8,
              borderRadius: 4,
              bgcolor: "#f0f0f0",
              "& .MuiLinearProgress-bar": { bgcolor: "#000" },
            }}
          />

          {/* Step 1: Service Type Selection */}
          {step === 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h4" sx={{ mb: 4, fontWeight: 700, textAlign: "center", color: "#000" }}>
                Choose Your Service Type
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      height: "100%",
                      border: serviceType === "recruitment" ? "3px solid #000" : "2px solid #e0e0e0",
                      bgcolor: serviceType === "recruitment" ? "#f8f8f8" : "white",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 4,
                      },
                    }}
                    onClick={() => handleServiceTypeSelect("recruitment")}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                        <Avatar sx={{ bgcolor: "#000", mr: 2, width: 64, height: 64 }}>
                          <BusinessIcon sx={{ fontSize: 32 }} />
                        </Avatar>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: "#000" }}>
                          Recruitment (Auto)
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ mb: 3, color: "#666", lineHeight: 1.6 }}>
                        AI-powered automated recruitment solutions for efficient hiring processes with real-time
                        analytics and seamless integration.
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        <Chip label="Quick Setup" variant="outlined" sx={{ borderColor: "#000", color: "#000" }} />
                        <Chip label="Automated" variant="outlined" sx={{ borderColor: "#000", color: "#000" }} />
                        <Chip label="Cost-effective" variant="outlined" sx={{ borderColor: "#000", color: "#000" }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      height: "100%",
                      border: serviceType === "hr-outsourcing" ? "3px solid #000" : "2px solid #e0e0e0",
                      bgcolor: serviceType === "hr-outsourcing" ? "#f8f8f8" : "white",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 4,
                      },
                    }}
                    onClick={() => handleServiceTypeSelect("hr-outsourcing")}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                        <Avatar sx={{ bgcolor: "#000", mr: 2, width: 64, height: 64 }}>
                          <PeopleIcon sx={{ fontSize: 32 }} />
                        </Avatar>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: "#000" }}>
                          HR Outsourcing
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ mb: 3, color: "#666", lineHeight: 1.6 }}>
                        Comprehensive HR solutions with 11 specialized service categories, fully customizable to your
                        business requirements.
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        <Chip label="11 Categories" variant="outlined" sx={{ borderColor: "#000", color: "#000" }} />
                        <Chip label="Customizable" variant="outlined" sx={{ borderColor: "#000", color: "#000" }} />
                        <Chip label="Expert Support" variant="outlined" sx={{ borderColor: "#000", color: "#000" }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Step 2: Project Details (for Recruitment) */}
          {step === 1 && serviceType === "recruitment" && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h4" sx={{ mb: 4, fontWeight: 700, textAlign: "center", color: "#000" }}>
                Project Details
              </Typography>

              <Paper sx={{ p: 4, border: "1px solid #e0e0e0" }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Project Name"
                      required
                      value={projectData.projectName}
                      onChange={(e) => setProjectData((prev) => ({ ...prev, projectName: e.target.value }))}
                      placeholder="Enter your project name"
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": { borderColor: "#e0e0e0" },
                          "&:hover fieldset": { borderColor: "#000" },
                          "&.Mui-focused fieldset": { borderColor: "#000" },
                        },
                        "& .MuiInputLabel-root.Mui-focused": { color: "#000" },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={availableTags}
                      value={projectData.tags}
                      onChange={(_, newValue) => setProjectData((prev) => ({ ...prev, tags: newValue }))}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            key={index}
                            variant="outlined"
                            label={option}
                            {...getTagProps({ index })}
                            sx={{ borderColor: "#000", color: "#000" }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Tags"
                          placeholder="Add tags (press Enter to create custom tags)"
                          required
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": { borderColor: "#e0e0e0" },
                              "&:hover fieldset": { borderColor: "#000" },
                              "&.Mui-focused fieldset": { borderColor: "#000" },
                            },
                            "& .MuiInputLabel-root.Mui-focused": { color: "#000" },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel sx={{ "&.Mui-focused": { color: "#000" } }}>Budget Range</InputLabel>
                      <Select
                        value={projectData.budget}
                        label="Budget Range"
                        onChange={(e) => setProjectData((prev) => ({ ...prev, budget: e.target.value }))}
                        sx={{
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e0e0e0" },
                          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#000" },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#000" },
                        }}
                      >
                        <MenuItem value="under-5k">Under N15,000</MenuItem>
                        <MenuItem value="15k-85k">N15,000 - N85,000</MenuItem>
                        <MenuItem value="15k-50k">N95,000 - #150,000</MenuItem>
                        <MenuItem value="50k-100k">N150,000 - N300,000</MenuItem>
                        <MenuItem value="over-300k">Over N300,000</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel sx={{ "&.Mui-focused": { color: "#000" } }}>Urgency Level</InputLabel>
                      <Select
                        value={projectData.urgency}
                        label="Urgency Level"
                        onChange={(e) =>
                          setProjectData((prev) => ({ ...prev, urgency: e.target.value as ProjectData["urgency"] }))
                        }
                        sx={{
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e0e0e0" },
                          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#000" },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#000" },
                        }}
                      >
                        <MenuItem value="low">Low - Within 30 days</MenuItem>
                        <MenuItem value="medium">Medium - Within 2 weeks</MenuItem>
                        <MenuItem value="high">High - ASAP</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Timeline"
                      value={projectData.timeline}
                      onChange={(e) => setProjectData((prev) => ({ ...prev, timeline: e.target.value }))}
                      placeholder="Expected project timeline (e.g., 3 months, 6 weeks)"
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": { borderColor: "#e0e0e0" },
                          "&:hover fieldset": { borderColor: "#000" },
                          "&.Mui-focused fieldset": { borderColor: "#000" },
                        },
                        "& .MuiInputLabel-root.Mui-focused": { color: "#000" },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Project Description"
                      multiline
                      rows={4}
                      value={projectData.description}
                      onChange={(e) => setProjectData((prev) => ({ ...prev, description: e.target.value }))}
                      placeholder="Describe your project requirements, goals, and any specific details..."
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": { borderColor: "#e0e0e0" },
                          "&:hover fieldset": { borderColor: "#000" },
                          "&.Mui-focused fieldset": { borderColor: "#000" },
                        },
                        "& .MuiInputLabel-root.Mui-focused": { color: "#000" },
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          )}

          {/* Step 2: HR Outsourcing Service Selection */}
          {step === 1 && serviceType === "hr-outsourcing" && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h4" sx={{ mb: 4, fontWeight: 700, textAlign: "center", color: "#000" }}>
                Select HR Services
              </Typography>

              <Paper sx={{ maxHeight: "60vh", overflowY: "auto", p: 2, border: "1px solid #e0e0e0" }}>
                {hrOutsourcingServices.map((service) => (
                  <Accordion
                    key={service.id}
                    expanded={selectedServices.includes(service.id)}
                    sx={{
                      mb: 2,
                      border: "2px solid #e0e0e0",
                      borderRadius: 2,
                      "&:before": { display: "none" },
                      boxShadow: "none",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      onClick={() => handleServiceToggle(service.id)}
                      sx={{
                        bgcolor: selectedServices.includes(service.id) ? "#f8f8f8" : "white",
                        borderRadius: 2,
                        "&:hover": { bgcolor: "#f5f5f5" },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedServices.includes(service.id)}
                              sx={{ color: "#000", "&.Mui-checked": { color: "#000" } }}
                            />
                          }
                          label=""
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Box sx={{ ml: 1 }}>
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 600, display: "flex", alignItems: "center", color: "#000" }}
                          >
                            <span style={{ fontSize: "1.5rem", marginRight: "12px" }}>{service.icon}</span>
                            {service.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {service.description}
                          </Typography>
                        </Box>
                      </Box>
                    </AccordionSummary>

                    {selectedServices.includes(service.id) && (
                      <AccordionDetails sx={{ pt: 0, bgcolor: "#fafafa" }}>
                        <Divider sx={{ mb: 3 }} />
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: "#000" }}>
                          Select specific services:
                        </Typography>
                        <Grid container spacing={1}>
                          {serviceItems[service.id]?.map((item) => (
                            <Grid item xs={12} md={6} key={item.id}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={item.selected}
                                    onChange={() => handleChecklistToggle(service.id, item.id)}
                                    sx={{ color: "#000", "&.Mui-checked": { color: "#000" } }}
                                  />
                                }
                                label={
                                  <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                                    {item.name}
                                  </Typography>
                                }
                                sx={{ width: "100%", m: 0 }}
                              />
                            </Grid>
                          ))}
                        </Grid>

                        {serviceItems[service.id]?.some((item) => item.id.includes("others") && item.selected) && (
                          <Box sx={{ mt: 3 }}>
                            <TextField
                              fullWidth
                              multiline
                              rows={3}
                              placeholder="Please specify your custom requirements..."
                              value={customRequirements[service.id] || ""}
                              onChange={(e) => handleCustomRequirementChange(service.id, e.target.value)}
                              variant="outlined"
                              size="small"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  "& fieldset": { borderColor: "#000" },
                                  "&:hover fieldset": { borderColor: "#000" },
                                  "&.Mui-focused fieldset": { borderColor: "#000" },
                                },
                              }}
                            />
                          </Box>
                        )}
                      </AccordionDetails>
                    )}
                  </Accordion>
                ))}
              </Paper>
            </Box>
          )}
        </Box>
      </DialogContent>

      {/* Navigation Buttons */}
      <DialogActions sx={{ p: 3, borderTop: "1px solid #e0e0e0", justifyContent: "space-between" }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => setStep(Math.max(0, step - 1))}
          disabled={step === 0}
          sx={{
            color: "#000",
            borderColor: "#000",
            "&:hover": { borderColor: "#000", bgcolor: "#f5f5f5" },
          }}
        >
          Previous
        </Button>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              color: "#666",
              borderColor: "#666",
              "&:hover": { borderColor: "#666", bgcolor: "#f5f5f5" },
            }}
          >
            Cancel
          </Button>

          {step < steps.length - 1 ? (
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={() => setStep(step + 1)}
              disabled={!canProceed() || isSubmitting}
              sx={{
                bgcolor: "#000",
                color: "white",
                "&:hover": { bgcolor: "#333" },
                "&:disabled": { bgcolor: "#ccc" },
              }}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={isSubmitting ? null : <CheckIcon />}
              onClick={handleSubmit}
              disabled={!canProceed() || isSubmitting}
              sx={{
                bgcolor: "#000",
                color: "white",
                "&:hover": { bgcolor: "#333" },
                "&:disabled": { bgcolor: "#ccc" },
              }}
            >
              {isSubmitting ? "Submitting..." : "Submit Request"}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  )
}
